import { safeErr } from '../App';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateLeads, importLeads } from '../api';

const SECTORS = ['IT & Software','Healthcare','Manufacturing','Retail','Education','Logistics','Finance','Real Estate','Food & Agri'];
const SECTOR_ICONS = {'IT & Software':'💻','Healthcare':'🏥','Manufacturing':'🏭','Retail':'🛒','Education':'📚','Logistics':'🚚','Finance':'🏦','Real Estate':'🏠','Food & Agri':'🌾'};

const MARKETS = [
  { id:'US',    flag:'🇺🇸', label:'United States',  sub:'SF, NYC, Austin, Seattle',  color:'var(--blue)',   bg:'var(--blue-bg)',   border:'var(--blue-border)'  },
  { id:'EU',    flag:'🇪🇺', label:'Europe',          sub:'London, Berlin, Amsterdam', color:'var(--green)',  bg:'var(--green-bg)',  border:'var(--green-border)' },
  { id:'India', flag:'🇮🇳', label:'India',           sub:'Bangalore, Mumbai, Chennai',color:'var(--amber)',  bg:'var(--amber-bg)',  border:'var(--amber-border)' },
  { id:'All',   flag:'🌍', label:'All markets',     sub:'US + EU + India combined',  color:'var(--purple)', bg:'var(--purple-bg)', border:'var(--purple-border)'},
];

const FUNDING_STAGES = [
  { id:'Seed',     label:'Seed',       sub:'$0–2M raised',     icon:'🌱' },
  { id:'Series A', label:'Series A',   sub:'$2M–15M raised',   icon:'🚀' },
  { id:'Series B', label:'Series B',   sub:'$15M–50M raised',  icon:'📈' },
  { id:'Any',      label:'Any stage',  sub:'No filter',        icon:'✦'  },
];

const COMPANY_SIZES = [
  { id:'1-10',   label:'1–10',   sub:'Solo founders' },
  { id:'11-50',  label:'11–50',  sub:'Early stage'   },
  { id:'51-200', label:'51–200', sub:'Growth stage'  },
  { id:'Any',    label:'Any',    sub:'No filter'     },
];

const SOURCES = [
  { id:'Apollo',      icon:'🔍', label:'Apollo.io',     sub:'B2B contacts with verified emails'      },
  { id:'ProductHunt', icon:'🐱', label:'Product Hunt',  sub:'Founders who just launched today'       },
  { id:'JobPostings', icon:'💼', label:'Job Postings',  sub:'Companies hiring devs — active build signal' },
  { id:'HackerNews',  icon:'🔶', label:'Hacker News',   sub:'Founders posting "need a developer"'    },
];

const SOURCE_LABELS = { Apollo:'Apollo.io', ProductHunt:'Product Hunt', JobPostings:'job boards', HackerNews:'Hacker News' };

const MARKET_TIPS = {
  US:    'Founders at seed/Series A stage in the US are actively looking for dev partners. They have budget ($50k–$200k) and need to build fast.',
  EU:    'European startups in CleanTech, HealthTech and B2B SaaS are growing fast. London and Berlin have the highest density of funded startups.',
  India: 'Funded Indian startups in Fintech, Edtech and Logistics are your fastest close. Short sales cycle, no timezone gap.',
  All:   'Casting across all markets. Good for testing — narrow down once you see which market responds best.',
};

const FLAG_MAP = { US:'🇺🇸', EU:'🇪🇺', India:'🇮🇳' };

export default function GenerateLeads({ showToast }) {
  const [source,   setSource]   = useState('Apollo');
  const [market,   setMarket]   = useState('US');
  const [sectors,  setSectors]  = useState(['IT & Software']);
  const [funding,  setFunding]  = useState('Seed');
  const [size,     setSize]     = useState('1-10');
  const [num,      setNum]      = useState(20);
  const [found,    setFound]    = useState([]);
  const [sel,      setSel]      = useState(new Set());
  const [loading,  setLoading]  = useState(false);
  const [importing,setImporting]= useState(false);
  const [result,   setResult]   = useState(null);
  const [tab,      setTab]      = useState('filters'); // filters | results
  const nav = useNavigate();

  const toggleSector = s => setSectors(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);
  const toggleSel    = i => setSel(p => { const n=new Set(p); n.has(i)?n.delete(i):n.add(i); return n; });

  const [sourceError, setSourceError] = useState('');

  const doGenerate = async () => {
    setLoading(true); setFound([]); setSel(new Set()); setResult(null); setSourceError('');
    try {
      const r = await generateLeads({
        sectors, num_leads: num,
        market, funding_stage: funding, company_size: size,
        source,
      });
      const leads = r.data.leads;
      setSourceError(r.data.source_error || '');
      setFound(leads);
      setSel(new Set(leads.map((_,i)=>i)));
      setTab('results');
      if (leads.length) showToast(`✓ Found ${leads.length} leads`);
      else showToast(r.data.source_error || 'No leads found', 'error');
    } catch(e) {
      showToast(safeErr(e, 'Generation failed'), 'error');
    } finally { setLoading(false); }
  };

  const isDemo = found.length > 0 && found[0]?.source === 'Apollo (demo)';

  const doImport = async () => {
    const toImport = found.filter((_,i) => sel.has(i));
    if (!toImport.length) { showToast('Select at least one lead','error'); return; }
    if (isDemo && !window.confirm(
      `These are DEMO sample leads, not real Apollo results (the API returned nothing for your filters).\n\n` +
      `They are fictional people — importing and emailing them will bounce or reach the wrong person.\n\n` +
      `Import anyway for testing purposes?`
    )) return;
    setImporting(true);
    try {
      const r = await importLeads({ leads: toImport, campaign_name: `${market} ${sectors[0]} outreach` });
      setResult(r.data);
      showToast(`✓ ${r.data.imported} leads imported!`);
    } catch(e) { showToast('Import failed','error'); }
    finally { setImporting(false); }
  };

  const selAll  = () => setSel(new Set(found.map((_,i)=>i)));
  const selNone = () => setSel(new Set());

  const selectedMarket = MARKETS.find(m => m.id === market);

  return (
    <>
      {/* Tab bar */}
      <div style={{display:'flex',gap:0,marginBottom:16,border:'1px solid var(--border)',
        borderRadius:'var(--r-lg)',overflow:'hidden',background:'var(--surface2)',
        width:'fit-content'}}>
        {[['filters','⚙ Filters'],['results',`◎ Results${found.length>0?` (${found.length})`:''}` ]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{
            padding:'8px 20px',fontSize:13,fontWeight:tab===id?600:400,
            background:tab===id?'var(--surface)':'transparent',
            color:tab===id?'var(--text)':'var(--text-2)',
            border:'none',cursor:'pointer',
            borderRight:id==='filters'?'1px solid var(--border)':'none',
          }}>{label}</button>
        ))}
      </div>

      {tab === 'filters' && (
        <>
          {/* Source selector */}
          <div className="card" style={{marginBottom:14}}>
            <div className="card-header"><span className="card-title">Lead source</span></div>
            <div className="card-body">
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                {SOURCES.map(s => (
                  <div key={s.id} onClick={()=>setSource(s.id)} style={{
                    padding:'12px 14px',borderRadius:'var(--r-lg)',cursor:'pointer',
                    border:`2px solid ${source===s.id?'var(--blue)':'var(--border)'}`,
                    background:source===s.id?'var(--blue-bg)':'var(--surface)',
                    transition:'all .12s',
                  }}>
                    <div style={{fontSize:20,marginBottom:5}}>{s.icon}</div>
                    <div style={{fontWeight:600,fontSize:13,color:source===s.id?'var(--blue)':'var(--text)'}}>{s.label}</div>
                    <div style={{fontSize:11,color:'var(--text-2)',marginTop:2}}>{s.sub}</div>
                  </div>
                ))}
              </div>
              {source==='ProductHunt' && (
                <div className="alert alert-info" style={{marginTop:12,fontSize:12}}>
                  Fetches today's Product Hunt launches. Founders are active builders — high intent but no email until you enrich via Apollo. Requires a free Product Hunt API key in your .env file.
                </div>
              )}
              {source==='JobPostings' && (
                <div className="alert alert-info" style={{marginTop:12,fontSize:12}}>
                  Finds companies actively hiring Software Developers / Engineers / IT Managers — a direct
                  signal they have a build need right now. Each company is auto-enriched via Hunter.io to find a
                  decision-maker contact (not the role being hired for); leads where that lookup finds nobody
                  show "needs enrichment" until you fill in a contact manually. Requires a free Adzuna API key
                  (app ID + app key from developer.adzuna.com) and a free Hunter.io API key — both in Settings.
                </div>
              )}
              {source==='HackerNews' && (
                <div className="alert alert-info" style={{marginTop:12,fontSize:12}}>
                  Searches Ask HN / Show HN posts for founders explicitly saying they need a developer —
                  a more direct intent signal than a job ad. Posts linking to a real company site are
                  auto-enriched via Hunter.io to find a contact at that domain; posts linking to GitHub/Vercel/etc,
                  or with no link, show "needs enrichment". No API key needed — uses HN's free public search.
                </div>
              )}
            </div>
          </div>

          {(source === 'Apollo' || source === 'JobPostings') && (<>

            {/* Market selector */}
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <span className="card-title">Target market</span>
                <span style={{fontSize:11,color:'var(--text-2)'}}>50% US · 30% EU · 20% India</span>
              </div>
              <div className="card-body">
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:12}}>
                  {MARKETS.map(m => (
                    <div key={m.id} onClick={()=>setMarket(m.id)} style={{
                      padding:'12px 10px',borderRadius:'var(--r-lg)',cursor:'pointer',textAlign:'center',
                      border:`2px solid ${market===m.id?m.border:'var(--border)'}`,
                      background:market===m.id?m.bg:'var(--surface)',
                      transition:'all .12s',
                    }}>
                      <div style={{fontSize:24,marginBottom:4}}>{m.flag}</div>
                      <div style={{fontWeight:600,fontSize:12,color:market===m.id?m.color:'var(--text)'}}>{m.label}</div>
                      <div style={{fontSize:10,color:'var(--text-2)',marginTop:2,lineHeight:1.4}}>{m.sub}</div>
                    </div>
                  ))}
                </div>
                {/* Market tip */}
                <div style={{background:'var(--surface2)',border:'1px solid var(--border)',
                  borderRadius:'var(--r-sm)',padding:'9px 12px',fontSize:12,
                  color:'var(--text-2)',lineHeight:1.6}}>
                  💡 {MARKET_TIPS[market]}
                </div>
              </div>
            </div>

            {source === 'Apollo' && (<>
            {/* Funding stage */}
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <span className="card-title">Funding stage</span>
                <span style={{fontSize:11,color:'var(--text-2)'}}>Target startups at the right stage</span>
              </div>
              <div className="card-body">
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
                  {FUNDING_STAGES.map(f => (
                    <div key={f.id} onClick={()=>setFunding(f.id)} style={{
                      padding:'10px 8px',borderRadius:'var(--r)',cursor:'pointer',textAlign:'center',
                      border:`2px solid ${funding===f.id?'var(--blue)':'var(--border)'}`,
                      background:funding===f.id?'var(--blue-bg)':'var(--surface)',
                      transition:'all .12s',
                    }}>
                      <div style={{fontSize:18,marginBottom:3}}>{f.icon}</div>
                      <div style={{fontWeight:600,fontSize:12,color:funding===f.id?'var(--blue)':'var(--text)'}}>{f.label}</div>
                      <div style={{fontSize:10,color:'var(--text-2)',marginTop:1}}>{f.sub}</div>
                    </div>
                  ))}
                </div>
                {funding!=='Any' && (
                  <div style={{marginTop:10,fontSize:12,color:'var(--text-2)'}}>
                    {funding==='Seed' && '🎯 Best fit for Pickzy — seed startups have budget, urgency, and need to build their core product fast.'}
                    {funding==='Series A' && '🎯 Series A companies are scaling — they need product enhancements, integrations, and new features fast.'}
                    {funding==='Series B' && '⚠️ Series B companies are larger — longer sales cycles but higher deal values. Good for enterprise work.'}
                  </div>
                )}
              </div>
            </div>

            {/* Company size */}
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <span className="card-title">Company size</span>
                <span style={{fontSize:11,color:'var(--text-2)'}}>Smaller = easier to reach the decision maker directly</span>
              </div>
              <div className="card-body">
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
                  {COMPANY_SIZES.map(s => (
                    <div key={s.id} onClick={()=>setSize(s.id)} style={{
                      padding:'10px 8px',borderRadius:'var(--r)',cursor:'pointer',textAlign:'center',
                      border:`2px solid ${size===s.id?'var(--blue)':'var(--border)'}`,
                      background:size===s.id?'var(--blue-bg)':'var(--surface)',
                      transition:'all .12s',
                    }}>
                      <div style={{fontWeight:700,fontSize:15,color:size===s.id?'var(--blue)':'var(--text)'}}>{s.label}</div>
                      <div style={{fontSize:11,color:'var(--text-2)',marginTop:2}}>{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </>)}

            {/* Sectors */}
            <div className="card" style={{marginBottom:14}}>
              <div className="card-header">
                <span className="card-title">Target sectors</span>
                <span style={{fontSize:11,color:'var(--text-2)'}}>{sectors.length} selected</span>
              </div>
              <div className="card-body">
                <div className="chips-grid">
                  {SECTORS.map(s => (
                    <div key={s} className={`chip ${sectors.includes(s)?'selected':''}`}
                      onClick={()=>toggleSector(s)}>
                      <span>{SECTOR_ICONS[s]}</span>{s}
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </>)}

          {/* Number of leads + Generate button */}
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <label style={{fontSize:12,fontWeight:600,color:'var(--text-2)'}}>LEADS TO FIND</label>
              <select className="form-select" style={{width:90}} value={num}
                onChange={e=>setNum(parseInt(e.target.value))}>
                {[10,20,25,50].map(n=><option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" style={{gap:7}}
              disabled={loading || ((source==='Apollo'||source==='JobPostings') && !sectors.length)}
              onClick={doGenerate}>
              {loading
                ? <><span className="spinner spinner-sm"/>Searching {SOURCE_LABELS[source]}…</>
                : <>✦ Find {num} leads — {selectedMarket?.flag} {selectedMarket?.label}</>}
            </button>
          </div>

          {/* Summary of selected filters */}
          {(source==='Apollo'||source==='JobPostings') && (
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
              <span style={{fontSize:11,color:'var(--text-2)'}}>Searching:</span>
              <span className="pill pill-blue" style={{fontSize:10}}>{selectedMarket?.flag} {market}</span>
              {source==='Apollo' && <span className="pill pill-amber" style={{fontSize:10}}>{funding} stage</span>}
              {source==='Apollo' && <span className="pill pill-gray" style={{fontSize:10}}>{size} employees</span>}
              {sectors.slice(0,3).map(s=>(
                <span key={s} className="pill pill-green" style={{fontSize:10}}>{SECTOR_ICONS[s]} {s}</span>
              ))}
              {sectors.length>3 && <span className="pill pill-gray" style={{fontSize:10}}>+{sectors.length-3} more</span>}
            </div>
          )}
        </>
      )}

      {tab === 'results' && (
        <>
          {result ? (
            <div className="alert alert-success" style={{marginBottom:14,justifyContent:'space-between',alignItems:'center'}}>
              <span>✓ {result.imported} leads imported to SalesAI{result.skipped>0?` (${result.skipped} duplicates skipped)`:''}</span>
              <button className="btn btn-primary btn-sm" onClick={()=>nav('/leads')}>View leads →</button>
            </div>
          ) : found.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">◉</div>
              <div className="empty-title">No leads yet</div>
              <div className="empty-desc">
                {sourceError
                  ? <>Could not reach {source}: <strong>{sourceError}</strong></>
                  : 'Set your filters and click Generate to find leads'}
              </div>
              <button className="btn btn-primary" onClick={()=>setTab('filters')}>⚙ Set filters</button>
            </div>
          ) : (
            <>
              {isDemo && (
                <div className="alert alert-error" style={{marginBottom:14,fontWeight:600}}>
                  ⚠ DEMO DATA — {sourceError || 'Apollo returned no real results for these filters.'} These
                  are fictional sample people for testing the UI only. Broaden your filters and re-generate
                  before importing real leads.
                </div>
              )}
              {/* Results toolbar */}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontWeight:700,fontSize:15}}>{found.length} leads found</span>
                  <div style={{display:'flex',gap:5}}>
                    {['US','EU','India'].map(m=>{
                      const count = found.filter(l=>l.market===m).length;
                      if (!count) return null;
                      return <span key={m} className="pill pill-blue" style={{fontSize:10}}>{FLAG_MAP[m]||'🌍'} {m}: {count}</span>;
                    })}
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-sm" onClick={sel.size===found.length?selNone:selAll}>
                    {sel.size===found.length?'Deselect all':`Select all (${found.length})`}
                  </button>
                  <button className="btn btn-sm" onClick={()=>setTab('filters')}>⚙ Edit filters</button>
                  {!result && (
                    <button className="btn btn-primary btn-sm" disabled={importing||!sel.size}
                      onClick={doImport}>
                      {importing
                        ? <><span className="spinner spinner-sm"/>Importing…</>
                        : `↓ Import ${sel.size} leads`}
                    </button>
                  )}
                </div>
              </div>

              {/* Results table */}
              <div className="card" style={{marginBottom:0}}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{width:36}}/>
                        <th style={{width:60}}>Score</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Role</th>
                        <th>Email</th>
                        <th>Market</th>
                        <th>Funding</th>
                        <th>Sector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {found.map((l,i) => (
                        <tr key={i} className={sel.has(i)?'selected':''}
                          onClick={()=>toggleSel(i)}>
                          <td>
                            <input type="checkbox" checked={sel.has(i)} onChange={()=>{}}
                              style={{accentColor:'var(--blue)',cursor:'pointer'}}/>
                          </td>
                          {/* AI Score */}
                          <td>
                            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                              <div style={{
                                width:30,height:30,borderRadius:'50%',
                                display:'flex',alignItems:'center',justifyContent:'center',
                                fontSize:12,fontWeight:700,
                                background: l.score>=8?'var(--green-bg)':l.score>=5?'var(--amber-bg)':'var(--red-bg)',
                                color:       l.score>=8?'var(--green)': l.score>=5?'var(--amber)': 'var(--red)',
                              }}>{l.score||'?'}</div>
                              <span style={{fontSize:9,color:'var(--text-3)',
                                textTransform:'uppercase',letterSpacing:'.04em',lineHeight:1}}>
                                {l.priority||'med'}
                              </span>
                            </div>
                          </td>
                          <td style={{fontWeight:600}}>
                            <div>
                              {l.name || <span style={{fontWeight:400,fontStyle:'italic',color:'var(--text-3)'}}>(no contact found)</span>}
                              {isDemo && <span className="pill pill-red" style={{fontSize:9,marginLeft:6}}>DEMO</span>}
                            </div>
                            {l.reason && <div style={{fontSize:10,color:'var(--text-3)',fontWeight:400,marginTop:1,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={l.reason}>{l.reason}</div>}
                          </td>
                          <td>
                            <div style={{fontWeight:500}}>{l.company}</div>
                            {(l.tagline || l.signal) && <div style={{fontSize:10,color:'var(--text-2)',marginTop:1,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.tagline || l.signal}</div>}
                          </td>
                          <td style={{color:'var(--text-2)',fontSize:12}}>{l.role}</td>
                          <td>
                            {l.email
                              ? <span className="mono" style={{color:'var(--text-2)',fontSize:11}}>{l.email}</span>
                              : <span style={{fontSize:10,color:'var(--amber)',fontWeight:500}}>⚠ needs enrichment</span>}
                          </td>
                          <td>
                            <span style={{fontSize:14}}>{FLAG_MAP[l.market]||'🌍'}</span>
                            <span style={{fontSize:11,color:'var(--text-2)',marginLeft:4}}>{l.location}</span>
                          </td>
                          <td>
                            {l.funding
                              ? <span className={`pill ${l.funding.includes('Seed')||l.funding.includes('Pre')?'pill-green':l.funding.includes('Series A')?'pill-blue':'pill-amber'}`} style={{fontSize:10}}>{l.funding}</span>
                              : <span style={{fontSize:11,color:'var(--text-3)'}}>—</span>}
                          </td>
                          <td><span className="pill pill-blue" style={{fontSize:10}}>{SECTOR_ICONS[l.sector]} {l.sector}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Source note */}
              {!isDemo && (
                <div style={{fontSize:11,color:'var(--text-3)',marginTop:8,textAlign:'right'}}>
                  Source: {found[0]?.source || source}
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
